import { useNavigate } from "react-router";

/**
 * Maps old view-name strings (used by component onNavigate callbacks)
 * to URL paths. Single source of truth for the view→route mapping.
 */
const VIEW_TO_PATH: Record<string, string> = {
  // Core user journey
  home: "/",
  lookup: "/search",
  search: "/search",
  dashboard: "/dashboard",
  cases: "/cases",
  wizard: "/wizard",
  pricing: "/pricing",
  payment: "/dashboard",
  "appeal-hq": "/appeal-hq",
  "filing-guide": "/filing-guide",
  hearing: "/hearing",
  "hearing-mode": "/dev/hearing-mode",
  "download-center": "/download-center",
  "outcome-survey": "/outcome-survey",

  // Post-purchase & monitoring
  portal: "/dashboard",
  shield: "/shield/dashboard",
  "glass-box": "/glass-box",

  // Marketing & content
  about: "/about",
  faq: "/faq",
  blog: "/blog",
  "blog-post": "/blog/latest",
  "suburb-landing": "/suburb/katy",
  contact: "/contact",
  "decision-landing": "/property/demo",
  "how-it-works": "/#how-it-works",

  // Auth
  login: "/login",
  "sign-in": "/login",
  signup: "/signup",
  "sign-up": "/signup",
  "forgot-password": "/forgot-password",

  // Account
  "account-settings": "/account/settings",
  account: "/account/settings",
  billing: "/account/billing",
  "help-center": "/help",
  help: "/help",
  "reset-password": "/reset-password",
  "reset-expired": "/reset-password/expired",
  unsubscribe: "/unsubscribe",

  // Legal
  terms: "/terms",
  privacy: "/privacy",

  // Admin
  admin: "/admin",

  // Evidence components
  comps: "/comps",
  condition: "/condition",
  exemptions: "/exemptions",

  // Error/status
  "not-found": "/not-found-demo",
  "server-error": "/error",
  maintenance: "/maintenance",

  // Dev-only
  cards: "/dev/cards",
  "tax-summary": "/dev/tax-summary",
  calculator: "/dev/calculator",
  reality: "/dev/reality",
  "mobile-wizard": "/dev/mobile-wizard",
  commercial: "/dev/commercial",
  "outcome-trainer": "/dev/outcome-trainer",
  "error-states": "/dev/error-states",
  "repair-cost-prototype": "/dev/repair-cost-prototype",
  "comp-selector": "/dev/comp-selector",
  postcard: "/dev/postcard",
  emails: "/dev/emails",
  "packet-cover": "/dev/packet-cover",
  "loading-states": "/dev/loading-states",
  toasts: "/dev/toasts",
  forms: "/dev/forms",
  "evidence-selection": "/dev/evidence-selection",
  shell: "/dev/shell",
};

export function useAppNavigate() {
  const navigate = useNavigate();

  /** Drop-in replacement for `onNavigate: (view: string) => void` */
  const appNavigate = (viewName: string) => {
    const path = VIEW_TO_PATH[viewName];
    if (path) {
      if (path.startsWith("/#")) {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(path.slice(2));
          el?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        navigate(path);
      }
    } else {
      console.warn(`[useAppNavigate] Unknown view name: "${viewName}"`);
      navigate("/");
    }
  };

  /** Drop-in replacement for `onNavigateHome: () => void` */
  const navigateHome = () => navigate("/");

  return { appNavigate, navigateHome, navigate };
}
